// Import classes for use in functions below
import { Mob } from './Mob';
import { 
    MeleeMob,
    ZombieMob,
    DummyMob,
    BombZombieMob,
    JumpZombieMob
} from './MeleeMob';
import {
    RangedMob,
    SkeletonArcherMob,
    PoisonZombieMob,
    StationaryRangedMob
} from './RangedMob';
import {
    BossMob,
    SkeletonKnightBoss
} from './BossMob';

// Base classes
export { StateMachine, State } from './StateMachine';
export { Mob } from './Mob';

// State classes
export {
    IdleState,
    WalkState,
    PatrolState,
    AttackState,
    HurtState,
    DeathState,
    SkillState,
    JumpState
} from './MobStates';

// Mob type classes
export {
    MeleeMob,
    ZombieMob,
    DummyMob,
    BombZombieMob,
    JumpZombieMob
} from './MeleeMob';

export {
    RangedMob,
    SkeletonArcherMob,
    PoisonZombieMob,
    StationaryRangedMob
} from './RangedMob';

export {
    BossMob,
    SkeletonKnightBoss
} from './BossMob';

// Utility function to create mobs based on type
export function createMob(scene, x, y, mobType, config = {}) {
    try {
        let mob;
        switch (mobType) {
            case 'zombie':
                mob = new ZombieMob(scene, x, y, config);
                break;
            case 'dummy':
                mob = new DummyMob(scene, x, y, config);
                break;
            case 'bomb_zombie':
                mob = new BombZombieMob(scene, x, y, config);
                break;
            case 'jump_zombie':
                mob = new JumpZombieMob(scene, x, y, config);
                break;
            case 'skeleton_archer':
                mob = new SkeletonArcherMob(scene, x, y, config);
                break;
            case 'poison_zombie':
                mob = new PoisonZombieMob(scene, x, y, config);
                break;
            case 'skeleton_knight':
                mob = new SkeletonKnightBoss(scene, x, y, config);
                break;
            case 'stationary_ranged':
                mob = new StationaryRangedMob(scene, x, y, config);
                break;
            default:
                console.warn(`Unknown mob type: ${mobType}`);
                mob = new ZombieMob(scene, x, y, config); // Safe fallback
                break;
        }
        
        return mob;
    } catch (error) {
        console.error(`Failed to create mob of type '${mobType}':`, error);
        return null;
    }
}

// Mob spawner utility
export class MobSpawner {
    constructor(scene, x, y, config = {}) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        
        this.mobType = config.mobType || 'zombie';
        this.spawnInterval = config.spawnInterval || 5000; // 5 seconds
        this.maxMobs = config.maxMobs || 3;
        this.spawnRadius = config.spawnRadius || 20;
        this.isActive = config.isActive !== false;
        
        this.spawnedMobs = [];
        this.lastSpawnTime = 0;
        
        // Visual indicator (optional)
        if (config.showIndicator) {
            this.indicator = scene.add.circle(x, y, 10, 0xff0000, 0.3);
            this.indicator.setDepth(5);
        }
    }

    update() {
        if (!this.isActive) return;
        
        const currentTime = this.scene.time.now;
        
        // Only log every 10 seconds to avoid spam
        if (this.scene.time.now % 10000 < 50) {
            console.log(`MobSpawner: ${this.spawnedMobs.length}/${this.maxMobs} active mobs`);
        }
        
        // Clean up destroyed mobs
        const beforeCleanup = this.spawnedMobs.length;
        this.spawnedMobs = this.spawnedMobs.filter(mob => {
            const isAlive = mob.isAlive();
            if (!isAlive) {
                console.log(`Removing dead mob: ${mob.mobType}`);
            }
            return isAlive;
        });
        const afterCleanup = this.spawnedMobs.length;
        
        if (beforeCleanup !== afterCleanup) {
            console.log(`MobSpawner cleanup: ${beforeCleanup} -> ${afterCleanup} mobs`);
        }
        
        // Check if we can spawn more mobs
        if (this.spawnedMobs.length < this.maxMobs && 
            currentTime - this.lastSpawnTime >= this.spawnInterval) {
            
            this.spawnMob();
            this.lastSpawnTime = currentTime;
        }
    }

    spawnMob() {
        // Random position within spawn radius
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * this.spawnRadius;
        const spawnX = this.x + Math.cos(angle) * distance;
        const spawnY = this.y + Math.sin(angle) * distance;
        
        //console.log(`MobSpawner spawning ${this.mobType} at (${Math.round(spawnX)}, ${Math.round(spawnY)})`);
        const mob = createMob(this.scene, spawnX, spawnY, this.mobType);
        
        if (mob) {
            //console.log(`Successfully created ${mob.mobType} at (${Math.round(mob.x)}, ${Math.round(mob.y)})`);
            this.spawnedMobs.push(mob);
            
            // Play spawn animation if available
            mob.playAnimation('birth');
            
            // Set up birth animation completion event
            if (mob.sprite && mob.sprite.anims) {
                mob.sprite.once('animationcomplete', (anim) => {
                    if (anim.key.includes('birth')) {
                        mob.stateMachine.changeState('IdleState');
                    }
                });
            }
        } else {
            console.warn(`Failed to create mob of type ${this.mobType}`);
        }
        
        return mob;
    }

    activate() {
        this.isActive = true;
    }

    deactivate() {
        this.isActive = false;
    }

    destroy() {
        // Clean up spawned mobs
        this.spawnedMobs.forEach(mob => mob.destroy());
        this.spawnedMobs = [];
        
        // Remove indicator
        if (this.indicator) {
            this.indicator.destroy();
        }
    }
} 